import React, {useState, useRef, useEffect, useCallback} from 'react';
import styled, {createGlobalStyle} from 'styled-components';
import {useIntl, FormattedMessage} from 'react-intl';
import {FlexRow, FlexColumn} from '@forecast-it/design-system';
import {useAIChatStream} from './useAIChatStream';
import {D, MIN_WIDTH, MAX_WIDTH, DEFAULT_WIDTH, CARD_BOTTOM} from './design-tokens';
import {SparkleIcon, CloseIcon, SendIcon, StopIcon} from './icons';
import {EmptyState} from './EmptyState';
import {MessageBubble} from './MessageBubble';

export {DEFAULT_WIDTH};

// ── Global keyframe animations ─────────────────────────────────────────────────

const AIChatGlobalStyles = createGlobalStyle`
	@keyframes ai-bounce  { 0%,80%,100%{transform:translateY(0);opacity:.3} 40%{transform:translateY(-4px);opacity:1} }
	@keyframes ai-blink   { 0%,100%{opacity:1} 50%{opacity:0} }
	@keyframes ai-pulse   { 0%,100%{opacity:.4} 50%{opacity:1} }
	@keyframes ai-fade    { from{opacity:0;transform:translateY(3px)} to{opacity:1;transform:translateY(0)} }
	@keyframes ai-spin    { to{transform:rotate(360deg)} }
	@keyframes ai-card-in { from{opacity:0;transform:translateY(12px) scale(0.97)} to{opacity:1;transform:translateY(0) scale(1)} }
	@keyframes ai-card-out { from{opacity:1;transform:translateY(0) scaleY(1)} to{opacity:0;transform:translateY(30px) scaleY(0.08)} }
`;

// ── Styled components ──────────────────────────────────────────────────────────

const PanelCard = styled(FlexColumn)<{width: number; isClosing: boolean}>`
	position: fixed;
	bottom: ${CARD_BOTTOM}px;
	right: 84px;
	width: ${({width}) => width}px;
	height: calc(100vh - 140px);
	max-height: 900px;
	min-height: 500px;
	background: ${D.bg};
	border-radius: 16px;
	border: 1px solid ${D.border};
	z-index: 300;
	font-family: 'neue-haas-grotesk-text', 'Inter', sans-serif;
	font-size: 13px;
	box-shadow: 0 8px 40px rgba(57, 38, 100, 0.18), 0 2px 8px rgba(0, 0, 0, 0.08);
	overflow: hidden;
	transform-origin: bottom center;
	animation: ${({isClosing}) =>
		isClosing ? 'ai-card-out 0.25s cubic-bezier(0.4,0,0.2,1) forwards' : 'ai-card-in 0.2s cubic-bezier(0.34,1.56,0.64,1)'};
`;

const ResizeHandle = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	width: 5px;
	height: 100%;
	cursor: ew-resize;
	z-index: 10;
`;

const Header = styled(FlexRow)`
	background: linear-gradient(135deg, #392664 0%, #6e0feb 100%);
	padding: 14px 16px 14px 18px;
	gap: 10px;
	flex: none;
	border-radius: 16px 16px 0 0;
	align-items: center;
`;

const HeaderAvatar = styled(FlexRow)<{isStreaming: boolean}>`
	width: 34px;
	height: 34px;
	border-radius: 10px;
	background: rgba(255, 255, 255, 0.15);
	align-items: center;
	justify-content: center;
	flex: none;
	box-shadow: ${({isStreaming}) => (isStreaming ? '0 0 0 2px rgba(255,255,255,0.5)' : 'none')};
	transition: box-shadow 0.3s;
`;

const HeaderMeta = styled(FlexColumn)`
	flex: 1;
	min-width: 0;
	justify-content: center;
`;

const HeaderTitle = styled.div`
	font-size: 14px;
	font-weight: 700;
	color: #ffffff;
	letter-spacing: 0.1px;
`;

const HeaderStatus = styled(FlexRow)`
	font-size: 11px;
	color: rgba(255, 255, 255, 0.75);
	align-items: center;
	gap: 4px;
	margin-top: 1px;
	flex: none;
`;

const StatusDotActive = styled.span`
	width: 6px;
	height: 6px;
	border-radius: 50%;
	background: #86efac;
	display: inline-block;
	animation: ai-pulse 1.4s ease-in-out infinite;
`;

const StatusDotIdle = styled.span`
	width: 6px;
	height: 6px;
	border-radius: 50%;
	background: rgba(255, 255, 255, 0.5);
	display: inline-block;
`;

const CloseBtn = styled.button`
	background: rgba(255, 255, 255, 0.12);
	border: none;
	color: rgba(255, 255, 255, 0.85);
	cursor: pointer;
	width: 28px;
	height: 28px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 8px;
	flex-shrink: 0;
	font-size: 14px;
	transition: background 0.15s;

	&:hover {
		background: rgba(255, 255, 255, 0.22);
	}
`;

const MessagesArea = styled.div`
	flex: 1;
	overflow-y: auto;
	padding: 14px 14px 8px;
	background: ${D.surface};
	position: relative;
`;

const ScrollToBottomBtn = styled.button`
	position: absolute;
	bottom: 12px;
	left: 50%;
	transform: translateX(-50%);
	background: ${D.bg};
	border: 1px solid ${D.border};
	border-radius: 9999px;
	padding: 4px 12px;
	font-size: 11px;
	color: ${D.textMuted};
	cursor: pointer;
	box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
	display: flex;
	align-items: center;
	gap: 4px;

	&:hover {
		background: ${D.surface};
	}
`;

const InputArea = styled.div`
	border-top: 1px solid ${D.border};
	padding: 10px 12px 12px;
	background: ${D.bg};
	flex-shrink: 0;
	border-radius: 0 0 16px 16px;
`;

const InputRow = styled(FlexRow)`
	gap: 8px;
	align-items: flex-end;
`;

const StyledTextArea = styled.textarea`
	flex: 1;
	padding: 8px 11px;
	border: 1px solid ${D.inputBorder};
	border-radius: 8px;
	font-size: 13px;
	resize: none;
	outline: none;
	background: ${D.inputBg};
	color: ${D.textPrimary};
	line-height: 1.5;
	font-family: inherit;
	transition: border-color 0.15s;

	&:focus {
		border-color: ${D.borderFocus};
	}

	&:disabled {
		opacity: 0.6;
	}
`;

const StopButton = styled.button`
	padding: 8px 13px;
	flex-shrink: 0;
	background: ${D.errorBg};
	border: 1px solid ${D.errorBorder};
	color: ${D.errorText};
	border-radius: 8px;
	font-weight: 600;
	font-size: 12px;
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 4px;
`;

const SendButton = styled.button<{active: boolean}>`
	padding: 8px 14px;
	flex-shrink: 0;
	background: ${({active}) => (active ? D.accentMid : D.surface)};
	color: ${({active}) => (active ? '#ffffff' : D.textDisabled)};
	border: 1px solid ${({active}) => (active ? D.accentMid : D.border)};
	border-radius: 8px;
	font-weight: 600;
	font-size: 12px;
	cursor: ${({active}) => (active ? 'pointer' : 'not-allowed')};
	display: flex;
	align-items: center;
	gap: 4px;
	transition: background 0.15s, border-color 0.15s;
`;

const ClearButton = styled.button<{active: boolean}>`
	padding: 8px 10px;
	flex-shrink: 0;
	background: transparent;
	color: ${({active}) => (active ? D.textMuted : D.textDisabled)};
	border: 1px solid ${({active}) => (active ? D.border : 'transparent')};
	border-radius: 8px;
	font-size: 12px;
	cursor: ${({active}) => (active ? 'pointer' : 'not-allowed')};
	display: flex;
	align-items: center;
	gap: 4px;
	transition: color 0.15s, border-color 0.15s, background 0.15s;

	&:hover {
		background: ${({active}) => (active ? D.surface : 'transparent')};
		color: ${({active}) => (active ? D.errorText : D.textDisabled)};
		border-color: ${({active}) => (active ? D.errorBorder : 'transparent')};
	}
`;

const FooterNote = styled.div`
	font-size: 10px;
	color: ${D.textDisabled};
	margin-top: 5px;
	text-align: center;
	letter-spacing: 0.2px;
`;

const NoApiKeyBanner = styled.div`
	background: ${D.errorBg};
	border: 1px solid ${D.errorBorder};
	color: ${D.errorText};
	border-radius: 8px;
	padding: 8px 12px;
	font-size: 12px;
	line-height: 1.4;
	margin-bottom: 8px;
`;

// ── Types ──────────────────────────────────────────────────────────────────────

type AIChatPanelProps = {
	apiKey: string;
	viewerPersonId?: number;
	onClose: () => void;
	history: {push: (path: string) => void} | null | undefined;
	onWidthChange?: (w: number) => void;
	isClosing: boolean;
};

// ── Component ──────────────────────────────────────────────────────────────────

const AIChatPanel = ({apiKey, viewerPersonId, onClose, history, onWidthChange, isClosing}: AIChatPanelProps): JSX.Element => {
	const [input, setInput] = useState('');
	const [showScrollBtn, setShowScrollBtn] = useState(false);
	const [width, setWidth] = useState(DEFAULT_WIDTH);
	const {messages, isStreaming, sendMessage, abort, clearMessages} = useAIChatStream({apiKey, viewerPersonId});
	const userMessageCount = messages.filter(m => m.role === 'user').length;
	const clearActive = userMessageCount > 3 && !isStreaming;
	const scrollRef = useRef<HTMLDivElement>(null);
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLTextAreaElement>(null);
	const dragStartX = useRef<number | null>(null);
	const dragStartWidth = useRef<number | null>(null);
	const {formatMessage} = useIntl();

	// ── Horizontal resize from left edge ────────────────────────────────────────
	const onResizeMouseDown = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			e.preventDefault();
			dragStartX.current = e.clientX;
			dragStartWidth.current = width;

			const onMouseMove = (mv: MouseEvent) => {
				const delta = (dragStartX.current ?? 0) - mv.clientX;
				const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, (dragStartWidth.current ?? width) + delta));
				setWidth(newWidth);
				if (onWidthChange) onWidthChange(newWidth);
			};
			const onMouseUp = () => {
				document.removeEventListener('mousemove', onMouseMove);
				document.removeEventListener('mouseup', onMouseUp);
				document.body.style.cursor = '';
				document.body.style.userSelect = '';
			};
			document.addEventListener('mousemove', onMouseMove);
			document.addEventListener('mouseup', onMouseUp);
			document.body.style.cursor = 'ew-resize';
			document.body.style.userSelect = 'none';
		},
		[width, onWidthChange]
	);

	// ── Scroll handling ──────────────────────────────────────────────────────────
	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;
		const onScroll = () => {
			const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
			setShowScrollBtn(distFromBottom > 120);
		};
		el.addEventListener('scroll', onScroll);
		return () => el.removeEventListener('scroll', onScroll);
	}, []);

	useEffect(() => {
		if (!showScrollBtn) {
			messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
		}
	}, [messages, showScrollBtn]);

	const scrollToBottom = useCallback(() => {
		messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
		setShowScrollBtn(false);
	}, []);

	// ── Send / keyboard ──────────────────────────────────────────────────────────
	const handleSend = useCallback(() => {
		const text = input.trim();
		if (!text || isStreaming) return;
		setInput('');
		void sendMessage(text, messages);
		setTimeout(() => inputRef.current?.focus(), 0);
	}, [input, isStreaming, sendMessage, messages]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				handleSend();
			}
		},
		[handleSend]
	);

	return (
		<>
			<AIChatGlobalStyles />
			<PanelCard width={width} isClosing={isClosing}>
				<ResizeHandle onMouseDown={onResizeMouseDown} />

				{/* ── Header ── */}
				<Header>
					<HeaderAvatar isStreaming={isStreaming}>
						<SparkleIcon size={18} color="#ffffff" />
					</HeaderAvatar>
					<HeaderMeta>
						<HeaderTitle>
							<FormattedMessage id="forecast_agent.header.title" />
						</HeaderTitle>
						<HeaderStatus>
							{isStreaming ? (
								<>
									<StatusDotActive />
									<FormattedMessage id="forecast_agent.header.status_working" />
								</>
							) : (
								<>
									<StatusDotIdle />
									<FormattedMessage id="forecast_agent.header.status_ready" />
								</>
							)}
						</HeaderStatus>
					</HeaderMeta>
					<CloseBtn
						type="button"
						onClick={onClose}
						aria-label={formatMessage({id: 'forecast_agent.header.close_label'})}
					>
						<CloseIcon />
					</CloseBtn>
				</Header>

				{/* ── Messages ── */}
				<MessagesArea ref={scrollRef}>
					{messages.length === 0 && <EmptyState onSuggestionClick={setInput} />}

					{messages.map((msg, idx) => (
						<MessageBubble
							key={msg.id}
							message={msg}
							isLastAssistant={msg.role === 'assistant' && idx === messages.length - 1 && isStreaming}
							history={history}
						/>
					))}
					<div ref={messagesEndRef} />

					{showScrollBtn && (
						<ScrollToBottomBtn type="button" onClick={scrollToBottom}>
							↓
						</ScrollToBottomBtn>
					)}
				</MessagesArea>

				{/* ── Input ── */}
				<InputArea>
					{!apiKey && (
						<NoApiKeyBanner>
							<FormattedMessage id="forecast_agent.no_api_key" />
						</NoApiKeyBanner>
					)}
					<InputRow>
						<StyledTextArea
							ref={inputRef}
							value={input}
							onChange={e => setInput(e.target.value)}
							onKeyDown={handleKeyDown}
							placeholder={formatMessage({id: 'forecast_agent.input.placeholder'})}
							disabled={isStreaming || !apiKey}
							rows={1}
						/>
						{isStreaming ? (
							<StopButton type="button" onClick={abort}>
								<StopIcon />
								<FormattedMessage id="forecast_agent.input.stop" />
							</StopButton>
						) : (
							<SendButton
								type="button"
								active={!!input.trim() && !!apiKey}
								onClick={handleSend}
								disabled={!input.trim() || !apiKey}
							>
								<SendIcon active={!!input.trim() && !!apiKey} />
								<FormattedMessage id="forecast_agent.input.send" />
							</SendButton>
						)}
						<ClearButton
							type="button"
							active={clearActive}
							onClick={clearActive ? clearMessages : undefined}
							disabled={!clearActive}
							title={clearActive ? formatMessage({id: 'forecast_agent.input.clear_title'}) : undefined}
						>
							<FormattedMessage id="forecast_agent.input.clear" />
						</ClearButton>
					</InputRow>
					<FooterNote>
						<FormattedMessage id="forecast_agent.input.footer" />
					</FooterNote>
				</InputArea>
			</PanelCard>
		</>
	);
};

export default AIChatPanel;
